import { useEffect, useState } from 'react';
import { useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Loading, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';

interface Overview {
  days: number;
  mailboxes: { id: number; address: string; country: string; provider: string; active: boolean; lastPolledAt: string | null; mails: number }[];
  funnel: { status: string; mails: number }[];
  stages: { stage: string; mails: number }[];
  totals: { mails: number; passedFilter: number; candidates: number; codes: number; costUsd: number; costPerCodeUsd: number | null; cachedCalls: number; paidCalls: number };
  budget: { scope: string; label: string; todayUsd: number; dailyCapUsd: number | null; monthUsd: number; monthlyCapUsd: number | null }[];
}

interface Sender {
  id: number;
  senderKey: string;
  displayName: string | null;
  sampleAddress: string | null;
  status: 'mapped' | 'parked' | 'ignored';
  merchant: { ref: string; name: string } | null;
  mappingSource: string | null;
  mailsSeen: number;
  mailsProcessed: number;
  mailsPassedFilter: number;
  codesFound: number;
  costUsd: number;
  costPerCodeUsd: number | null;
  sampleEvery: number;
  barrenStreak: number;
  lastCodeAt: string | null;
}

interface Mail {
  id: number;
  receivedAt: string;
  mailbox: string;
  sender: string | null;
  subject: string | null;
  status: string;
  stageReached: string | null;
  stopReason: string | null;
  mailType: string | null;
  prefilterScore: number | null;
  costUsd: number;
  candidatesFound: number;
}

interface Candidate {
  id: number;
  messageId: number;
  merchant: string | null;
  stage: string;
  kind: string;
  code: string | null;
  title: string;
  endsAt: string | null;
  verdict: 'ok' | 'review' | 'skip';
  verdictReasons: string[];
  confidence: number;
  evidence: string | null;
  seenCount: number;
  costUsd: number;
}

const TABS = ['senders', 'mails', 'candidates'] as const;
type Tab = (typeof TABS)[number];

const money = (usd: number | null) => (usd === null ? '–' : usd < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(2)}`);
const STATUS_TONE: Record<string, string> = { processed: 'badge-ok', duplicate: 'badge-none', filtered: 'badge-none', throttled: 'badge-none', parked: 'badge-review', budget_stopped: 'badge-warn', failed: 'badge-warn' };
const VERDICT_TONE: Record<string, string> = { ok: 'badge-ok', review: 'badge-review', skip: 'badge-warn' };

/**
 * The newsletter lane, for platform admins: where mails stop (most should stop early and free), which senders are
 * worth reading, what codes they gave and what each code cost.
 */
export function NewsletterSources() {
  const { query, setQuery } = useRouter();
  const tab: Tab = (TABS as readonly string[]).includes(query.get('tab') ?? '') ? (query.get('tab') as Tab) : 'senders';
  const changes = useChanges();
  const overview = useOp<Overview>('admin.inbox.overview', { days: 7 }, changes);

  useKeys({ '1': () => setQuery({ tab: 'senders' }, { replace: true }), '2': () => setQuery({ tab: 'mails' }, { replace: true }), '3': () => setQuery({ tab: 'candidates' }, { replace: true }) });

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Newsletter sources</h1>
          <p className="muted">Merchant newsletters in our country mailboxes. Every stage is cheaper than the next; only unclear parts reach a paid model. Nothing here goes live by itself.</p>
        </div>
      </header>
      {overview.error ? <ErrorBox error={overview.error} onRetry={overview.reload} /> : null}
      {!overview.data && !overview.error ? <Loading /> : null}
      {overview.data ? <OverviewPanel data={overview.data} /> : null}
      <div className="toolbar" role="tablist">
        {TABS.map((t, i) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={`btn btn-small ${tab === t ? 'btn-primary' : ''}`} onClick={() => setQuery({ tab: t }, { replace: true })}>
            {i + 1} {t === 'senders' ? 'Senders' : t === 'mails' ? 'Mails' : 'Offers found'}
          </button>
        ))}
      </div>
      {tab === 'senders' ? <Senders changes={changes} /> : tab === 'mails' ? <Mails changes={changes} /> : <Candidates changes={changes} />}
    </section>
  );
}

function OverviewPanel({ data }: { data: Overview }) {
  const t = data.totals;
  const stopped = (status: string) => data.funnel.find((f) => f.status === status)?.mails ?? 0;
  return (
    <div className="inbox-overview">
      <div className="inbox-stats" aria-label={`Last ${data.days} days`}>
        <Stat label={`Mails, last ${data.days} days`} value={String(t.mails)} />
        <Stat label="Stopped free (dupes, filter, parked, sampled)" value={String(stopped('duplicate') + stopped('filtered') + stopped('parked') + stopped('throttled'))} />
        <Stat label="Publishable codes" value={String(t.codes)} />
        <Stat label="Model spend" value={money(t.costUsd)} />
        <Stat label="Cost per code" value={money(t.costPerCodeUsd)} />
        <Stat label="Model calls (paid / cached)" value={`${t.paidCalls} / ${t.cachedCalls}`} />
      </div>
      <table className="table compact inbox-budget">
        <thead>
          <tr>
            <th>Budget</th>
            <th>Today</th>
            <th>Daily cap</th>
            <th>This month</th>
            <th>Monthly cap</th>
          </tr>
        </thead>
        <tbody>
          {data.budget.map((b) => (
            <tr key={`${b.scope}-${b.label}`} className={b.dailyCapUsd !== null && b.todayUsd >= b.dailyCapUsd ? 'selected' : ''}>
              <th>{b.label}</th>
              <td>{money(b.todayUsd)}</td>
              <td>{money(b.dailyCapUsd)}</td>
              <td>{money(b.monthUsd)}</td>
              <td>{money(b.monthlyCapUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.mailboxes.length === 0 ? (
        <Empty>No mailboxes yet. Add one per country (admin.inbox.saveMailbox); provider secrets stay in environment variables.</Empty>
      ) : (
        <p className="muted inbox-mailboxes">
          {data.mailboxes.map((m) => `${m.country} ${m.address} (${m.provider}${m.active ? '' : ', paused'}): ${m.mails} mails, polled ${m.lastPolledAt ? formatDate(m.lastPolledAt) : 'never'}`).join(' · ')}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="inbox-stat">
      <strong>{value}</strong>
      <span className="muted">{label}</span>
    </div>
  );
}

function Senders({ changes }: { changes: number }) {
  const [status, setStatus] = useState<'all' | 'mapped' | 'parked' | 'ignored'>('all');
  const senders = useOp<{ rows: Sender[] }>('admin.inbox.senders', { status }, changes);
  const rows = senders.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState<Sender | null>(null);
  useEffect(() => setCursor(0), [status]);
  useKeys({ j: () => setCursor((c) => step(c, 1, rows.length)), k: () => setCursor((c) => step(c, -1, rows.length)), o: () => rows[cursor] && setOpen(rows[cursor]!), enter: () => rows[cursor] && setOpen(rows[cursor]!) }, open === null);

  return (
    <>
      <div className="toolbar">
        <label className="inline-field">
          Show
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="all">All senders</option>
            <option value="parked">Waiting for a merchant</option>
            <option value="mapped">Mapped</option>
            <option value="ignored">Ignored</option>
          </select>
        </label>
      </div>
      {senders.error ? <ErrorBox error={senders.error} onRetry={senders.reload} /> : null}
      {senders.data && rows.length === 0 ? <Empty>No senders yet.</Empty> : null}
      {rows.length ? (
        <table className="table compact">
          <thead>
            <tr>
              <th>Sender</th>
              <th>Merchant</th>
              <th>Mails</th>
              <th>Passed filter</th>
              <th>Codes</th>
              <th>Cost per code</th>
              <th>Reading</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s, i) => (
              <tr key={s.id} className={`clickable ${i === cursor ? 'cursor' : ''}`} onClick={() => setOpen(s)}>
                <th>
                  {s.displayName ?? s.senderKey}
                  <div className="muted">{s.senderKey}</div>
                </th>
                <td>{s.merchant ? s.merchant.name : <span className={`badge ${s.status === 'ignored' ? 'badge-none' : 'badge-review'}`}>{s.status === 'ignored' ? 'ignored' : 'needs a merchant'}</span>}</td>
                <td>{s.mailsSeen}</td>
                <td>{s.mailsPassedFilter}</td>
                <td>{s.codesFound}</td>
                <td>{money(s.costPerCodeUsd)}</td>
                <td>{s.sampleEvery > 1 ? <span className="badge badge-warn">1 in {s.sampleEvery}</span> : 'every mail'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? <SenderDrawer sender={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function SenderDrawer({ sender, onClose }: { sender: Sender; onClose: () => void }) {
  const toast = useToast();
  const [q, setQ] = useState(sender.merchant?.name ?? sender.senderKey.replace(/^(plus|domain):/, '').split('.')[0] ?? '');
  const merchants = useOp<{ rows: { ref: string; name: string; slug: string }[] }>('admin.masterMerchants.list', { q, limit: 8 }, 0, q.trim().length >= 2);
  const update = (input: Record<string, unknown>, message: string) => runWrite(toast, 'admin.inbox.updateSender', { senderId: sender.id, ...input }, message).then((r) => r && onClose());

  return (
    <Drawer title={sender.displayName ?? sender.senderKey} onClose={onClose}>
      <dl className="facts">
        <dt>Sender</dt>
        <dd>
          {sender.senderKey} {sender.sampleAddress ? <span className="muted">({sender.sampleAddress})</span> : null}
        </dd>
        <dt>Merchant</dt>
        <dd>{sender.merchant ? `${sender.merchant.name} (mapped by ${sender.mappingSource ?? 'hand'})` : 'Not mapped: its mails wait unread.'}</dd>
        <dt>Yield</dt>
        <dd>
          {sender.mailsSeen} mails, {sender.mailsPassedFilter} passed the filter, {sender.codesFound} codes, {money(sender.costUsd)} spent
          {sender.lastCodeAt ? `, last code ${formatDate(sender.lastCodeAt)}` : ''}
        </dd>
        <dt>Reading</dt>
        <dd>{sender.sampleEvery > 1 ? `1 in ${sender.sampleEvery} mails (${sender.barrenStreak} mails in a row without a code)` : 'Every mail'}</dd>
      </dl>
      <label className="field">
        Map to a global merchant
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, slug or domain" autoFocus />
      </label>
      <ul className="pick-list">
        {(merchants.data?.rows ?? []).map((m) => (
          <li key={m.ref}>
            <button type="button" className="btn btn-small" onClick={() => update({ merchantRef: m.ref }, `${sender.senderKey} → ${m.name}`)}>
              {m.name} <span className="muted">{m.slug}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="toolbar">
        {sender.sampleEvery > 1 ? (
          <button type="button" className="btn btn-small" onClick={() => update({ resetThrottle: true }, 'Reading every mail again')}>
            Read every mail again
          </button>
        ) : null}
        {sender.status !== 'ignored' ? (
          <button type="button" className="btn btn-small" onClick={() => update({ status: 'ignored' }, `${sender.senderKey} ignored`)}>
            Ignore sender
          </button>
        ) : (
          <button type="button" className="btn btn-small" onClick={() => update({ status: sender.merchant ? 'mapped' : 'parked' }, `${sender.senderKey} back on`)}>
            Stop ignoring
          </button>
        )}
      </div>
    </Drawer>
  );
}

function Mails({ changes }: { changes: number }) {
  const [status, setStatus] = useState('');
  const mails = useOp<{ rows: Mail[] }>('admin.inbox.messages', status ? { status, limit: 200 } : { limit: 200 }, changes);
  const rows = mails.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  useKeys({ j: () => setCursor((c) => step(c, 1, rows.length)), k: () => setCursor((c) => step(c, -1, rows.length)), o: () => rows[cursor] && setOpen(rows[cursor]!.id), enter: () => rows[cursor] && setOpen(rows[cursor]!.id) }, open === null);

  return (
    <>
      <div className="toolbar">
        <label className="inline-field">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="processed">Processed</option>
            <option value="duplicate">Duplicate</option>
            <option value="filtered">Filtered</option>
            <option value="parked">Parked (unknown sender)</option>
            <option value="throttled">Sampled out</option>
            <option value="budget_stopped">Budget stop</option>
          </select>
        </label>
      </div>
      {mails.error ? <ErrorBox error={mails.error} onRetry={mails.reload} /> : null}
      {mails.data && rows.length === 0 ? <Empty>No mails.</Empty> : null}
      {rows.length ? (
        <table className="table compact">
          <thead>
            <tr>
              <th>Received</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Stopped at</th>
              <th>Offers</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={m.id} className={`clickable ${i === cursor ? 'cursor' : ''}`} onClick={() => setOpen(m.id)}>
                <td>{formatDate(m.receivedAt)}</td>
                <th>
                  {m.subject ?? '(no subject)'}
                  <div className="muted">
                    {m.sender ?? m.mailbox} · {m.mailType ?? '?'}
                  </div>
                </th>
                <td>
                  <span className={`badge ${STATUS_TONE[m.status] ?? ''}`}>{m.status.replace('_', ' ')}</span>
                </td>
                <td>
                  {m.stageReached}
                  {m.stopReason ? <div className="muted">{m.stopReason}</div> : null}
                </td>
                <td>{m.candidatesFound}</td>
                <td>{money(m.costUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open !== null ? <MailDrawer id={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function MailDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const detail = useOp<{ message: Mail; log: { stage: string; outcome: string; note: string; costMicros?: number }[]; candidates: Candidate[] }>('admin.inbox.message', { id });
  return (
    <Drawer title={detail.data?.message.subject ?? 'Mail'} onClose={onClose} wide>
      {detail.error ? <ErrorBox error={detail.error} /> : null}
      {!detail.data ? <Loading /> : null}
      {detail.data ? (
        <>
          <ol className="inbox-log">
            {detail.data.log.map((entry, i) => (
              <li key={i} className={`inbox-log-${entry.outcome}`}>
                <strong>{entry.stage}</strong> <span className="badge">{entry.outcome}</span> {entry.note}
                {entry.costMicros ? <span className="muted"> · {money(entry.costMicros / 1e6)}</span> : null}
              </li>
            ))}
          </ol>
          <CandidateTable rows={detail.data.candidates} />
        </>
      ) : null}
    </Drawer>
  );
}

function Candidates({ changes }: { changes: number }) {
  const [verdict, setVerdict] = useState<'all' | 'ok' | 'review' | 'skip'>('all');
  const candidates = useOp<{ rows: Candidate[] }>('admin.inbox.candidates', { verdict, limit: 200 }, changes);
  return (
    <>
      <div className="toolbar">
        <label className="inline-field">
          Verdict
          <select value={verdict} onChange={(e) => setVerdict(e.target.value as typeof verdict)}>
            <option value="all">All</option>
            <option value="ok">OK for the review queue</option>
            <option value="review">Needs a person</option>
            <option value="skip">Skipped (personal codes)</option>
          </select>
        </label>
      </div>
      {candidates.error ? <ErrorBox error={candidates.error} onRetry={candidates.reload} /> : null}
      {candidates.data ? <CandidateTable rows={candidates.data.rows} /> : null}
    </>
  );
}

function CandidateTable({ rows }: { rows: Candidate[] }) {
  if (!rows.length) return <Empty>No offers found.</Empty>;
  return (
    <table className="table compact">
      <thead>
        <tr>
          <th>Offer</th>
          <th>Merchant</th>
          <th>Found by</th>
          <th>Verdict</th>
          <th>Ends</th>
          <th>Seen</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id}>
            <th>
              {c.code ? <code>{c.code}</code> : null} {c.title}
              {c.evidence ? <div className="muted">“{c.evidence.slice(0, 160)}”</div> : null}
            </th>
            <td>{c.merchant ?? '–'}</td>
            <td>
              {c.stage}
              <div className="muted">{Math.round(c.confidence * 100)}% sure</div>
            </td>
            <td>
              <span className={`badge ${VERDICT_TONE[c.verdict]}`}>{c.verdict}</span>
              {c.verdictReasons.length ? <div className="muted">{c.verdictReasons.join('; ')}</div> : null}
            </td>
            <td>{c.endsAt ? formatDate(c.endsAt) : '–'}</td>
            <td>{c.seenCount}×</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
